import Controller from '@ember/controller';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { RoomsOrderBy, SortDirection } from 'garaje/graphql/generated/roomba-types';
import type { PaginationInput, RoomsPageFilterInput, RoomsSortOrder } from 'garaje/graphql/generated/roomba-types';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type RoombaGraphqlService from 'garaje/services/roomba-graphql';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

export type ResourceType = {
  id: number;
  label: string;
  count: number;
  icon: string;
  beta?: boolean;
};

export default class ResourcesResourceTypeListController extends Controller {
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;
  @service declare store: Store;
  @service declare roombaGraphql: RoombaGraphqlService;

  @tracked resourceTypes: ResourceType[] = [];

  loadResourceTypes = task({ drop: true }, async () => {
    const [desk, room, parking] = await Promise.all([
      this.getDeskResourceType.perform(),
      this.getRoomResourceType.perform(),
      this.getParkingResourceType.perform(),
    ]);

    this.resourceTypes = [desk, room, parking];
  });

  getRoomResourceType = task({ drop: true }, async () => {
    const resourceType = {
      id: 3,
      label: 'Room',
      icon: '/assets/images/resource-map/room-icon.svg',
      count: 0,
    };

    try {
      const sort: RoomsSortOrder = { by: RoomsOrderBy.DisplayName, direction: SortDirection.Asc };

      const filter: RoomsPageFilterInput = {
        locationId: { _eq: this.state.currentLocation.id, _in: null, _is_null: null, _neq: null, _nin: null },
        buildingId: null,
        buildingName: null,
        displayName: null,
        floorName: null,
      };

      const pagination: PaginationInput = {
        limit: 1,
        offset: 0,
      };

      const { totalCount } = await this.roombaGraphql.getRoomsPaginated(filter, sort, pagination);

      return {
        ...resourceType,
        count: totalCount || 0,
      };
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      return resourceType;
    }
  });

  getDeskResourceType = task({ drop: true }, async () => {
    const resourceType = {
      id: 1,
      label: 'Desk',
      icon: '/assets/images/resource-map/desk-icon.svg',
      count: 0,
    };

    try {
      const deskLocations = await this.store.query('desk-location', {
        reload: true,
        filter: {
          'location-id': this.state.currentLocation.id,
        },
        include: 'desk-counts',
      });

      return {
        ...resourceType,
        count: deskLocations?.toArray()?.[0]?.desksQuantity || 0,
      };
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      return resourceType;
    }
  });

  getParkingResourceType = task({ drop: true }, async () => {
    const resourceType = {
      id: 2,
      label: 'Parking',
      icon: '/assets/images/resource-map/parking-icon.svg',
      count: 0,
      beta: true,
    };

    return await Promise.resolve(resourceType);
  });
}
